import React, { cloneElement, ReactElement, useCallback, useLayoutEffect, useRef } from 'react';
import { useResizeObserver } from '@tanium/react-use-resize-observer';
import { HandleNewMeasurements } from './types';

interface Props {
  handleNewMeasurements: HandleNewMeasurements;
  index: number;
  item: ReactElement;
  size: number;
}

const ItemMeasurerInner = ({ handleNewMeasurements, index, item, size }: Props) => {
  const node = useRef<HTMLElement | null>(null);
  const currentSize = useRef<number>(size);
  currentSize.current = size;

  const measureItem = useCallback(
    (isMount: boolean) => {
      if (node.current instanceof HTMLElement) {
        const newSize = Math.ceil(node.current.offsetHeight);
        if (currentSize.current !== newSize || isMount) {
          handleNewMeasurements(index, newSize, isMount);
        }
      }
    },
    [handleNewMeasurements, index],
  );

  const onResize = useCallback(() => measureItem(false), [measureItem]);

  // Watch for resizes due to changed content,
  // Or changes in the size of the parent container.
  const itemRef = useResizeObserver(onResize);

  const refSetter = useCallback(
    (ref: HTMLElement) => {
      if (ref instanceof HTMLElement) {
        node.current = ref;
        itemRef(ref);
      }
    },
    [itemRef],
  );

  useLayoutEffect(() => {
    // Force sync measure for the initial mount.
    // This is necessary to support the DynamicSizeList layout logic.
    measureItem(true);
  }, [measureItem]);

  return cloneElement(item, {
    ref: refSetter,
  });
};

export const ItemMeasurer = React.memo(ItemMeasurerInner);
